<template>
  <div v-if="$store.state.home.user">
    <v-overlay
      :value="isAnyOpen"
      z-index="1010"
      class="main-overlay"
    ></v-overlay>

    <onboarding-choose-installation-type-modal
      :is-open="displayChooseInstallationTypeModal"
      :loading="submitting"
      :can-install-wordpress="canInstallWordpress"
      :can-install-from-git="canInstallFromGit"
      :can-install-from-files="canInstallFromFiles"
      :can-install-from-panelalpha-go="canInstallFromPanelalphaGo"
      @next="installationTypeSelected"
      @remove-modal="deactivateAll"
    />

    <onboarding-deploy-from-git-modal
      :is-open="displayDeployFromGitModal"
      :loading="submitting"
      @next="deployFromGit"
      @remove-modal="deactivateAll"
    />

    <onboarding-deploy-from-zip-modal
      :is-open="displayDeployFromZipModal"
      :loading="submitting"
      @next="installFromFilesProcess"
      @remove-modal="deactivateAll"
    />

    <onboarding-choose-creation-type-modal
      :is-open="displayChooseCreationTypeModal"
      :loading="submitting"
      @next="creationTypeSelected"
      @remove-modal="deactivateAll"
    />

    <onboarding-choose-service-modal
      :is-open="displayChooseServiceModal"
      :loading="submitting"
      :loading-text="loadingText"
      :is-import="creationType === 1"
      :installation-type="selectedInstallationType"
      :services="services"
      :selected-service.sync="selectedService"
      @go-back="goBack"
      @remove-modal="deactivateAll"
      @service-selected="serviceSelected"
    />

    <choose-onboarding-process
      :isOpen="displayChooseOnboardingTypeModal"
      @next="demoOnboardingSelected"
      @remove-modal="deactivateAll"
    />

    <onboarding-choose-template-modal
      :isOpen="displayChooseTemplate"
      :loading="submitting"
      :loading-text="loadingText"
      :selectedService="selectedService"
      @go-back="gotoDashboard"
      @theme-selected="themeSelected"
    />

    <onboarding-share-access-modal
      :isOpen="displayShareAccessModal"
      :loading="submitting"
      :loading-text="loadingText"
      :is-import="creationType === 1"
      :max-steps="importSteps"
      :services="services"
      :selected-service="selectedService"
      :selected-theme="selectedTheme"
      :installation-type="selectedInstallationType"
      @remove-modal="deactivateAll"
      @shared-access-set="sharedAccessSet"
      @change-service-button-clicked="changeServiceButtonClicked"
      @change-theme-button-clicked="changeThemeButtonClicked"
    />

    <onboarding-choose-user-configurable-options-modal
      :isOpen="displayChooseUserConfigurableOptionsModal"
      :loading="submitting"
      :loading-text="loadingText"
      :is-import="creationType === 1"
      :max-steps="importSteps"
      :services="services"
      :selected-service="selectedService"
      :selected-theme="selectedTheme"
      :user-configurable-option-values="userConfigurableOptions"
      @remove-modal="deactivateAll"
      @change-service-button-clicked="changeServiceButtonClicked"
      @change-theme-button-clicked="changeThemeButtonClicked"
      @user-configurable-options-selected="userConfigurableOptionsSelected"
    />

    <onboarding-choose-domain-modal
      :isOpen="displayWebsiteDomainModal"
      :loading="submitting || loading"
      :loading-text="loadingText"
      :services="services"
      :selected-service="selectedService"
      :selected-theme="selectedTheme"
      :installation-type="selectedInstallationType"
      @go-back="goBack"
      @remove-modal="deactivateAll"
      @change-service-button-clicked="changeServiceButtonClicked"
      @change-theme-button-clicked="changeThemeButtonClicked"
      @domain-selected="domainSelected"
    />

    <onboarding-domain-transfer-modal
      :is-open="displayDomainTransferModal"
      :loading="loading"
      :loading-text="loadingText"
      :services="services"
      :selectedService="selectedService"
      :selected-theme="selectedTheme"
      :domain="selectedDomainName"
      :domain-verification-record="domainTransferDnsRecord"
      @remove-modal="deactivateAll"
      @change-service-button-clicked="changeServiceButtonClicked"
      @verification-record-updated="domainTransferDnsRecord = $event"
      @transfer-domain="transferDomain"
    />

    <onboarding-domain-instant-verification-modal
      :is-open="displayDomainDnsVerificationModal"
      :loading="submitting"
      :selectedService="selectedService"
      :selected-theme="selectedTheme"
      :domain="selectedDomainName"
      :new-domain="newDomain"
      :installation-type="selectedInstallationType"
      @remove-modal="deactivateAll"
      @domain-verified="domainVerified"
    />

    <onboarding-choose-site-name-modal
      :is-open="displayChooseSiteNameModal"
      :loading="submitting"
      :services="services"
      :selected-service="selectedService"
      :selected-theme="selectedTheme"
      @remove-modal="deactivateAll"
      @change-service-button-clicked="changeServiceButtonClicked"
      @change-theme-button-clicked="changeThemeButtonClicked"
      @site-name-selected="siteNameSelected"
    />

    <onboarding-standard-form-modal
      :is-open="displayStandardForm"
      :loading="submitting"
      :selected-service="selectedService"
      @remove-modal="deactivateAll"
      @install-instance="installInstanceFromStandardForm"
      @retry-install-instance="retryInstallInstance"
    />

    <onboarding-installation-progress-modal
      :is-open="displayInstallationProgress"
      :theme="selectedTheme"
      :completed="installationCompleted"
      :error="installationError"
      :installedInstance="newInstance"
      @try-again="retryInstallInstance"
      @go-to-dashboard="gotoDashboard"
      @reset-installation="resetInstallation"
    />

    <onboarding-installation-super-quick-progress-modal
      :is-open="displaySuperQuickInstallationProgress"
      :selected-service="selectedService"
      :theme="selectedTheme"
      :completed="installationCompleted"
      :error="installationError"
      :installed-instance="installedInstance"
      :installed-instance-dns-mismatch="installedInstanceDnsMismatch"
      :service="selectedService"
      :show-start-creating-button="showStartCreatingButton"
      @go-to-dashboard="gotoDashboard"
      @go-to-wp-admin="wpAdminSsoRedirect(true)"
      @try-again="retryInstallInstance"
    />

    <onboarding-import-url-modal
      :is-open="displayImportUrlModal"
      :max-steps="importSteps"
      :loading="submitting"
      :loading-text="loadingText"
      @next="importedInstanceUrlSelected"
      @remove-modal="deactivateAll"
    />

    <onboarding-choose-import-method-modal
      :is-open="displayChooseImportMethodModal"
      :max-steps="importSteps"
      @import-method-selected="importMethodSelected"
      @remove-modal="deactivateAll"
    />

    <onboarding-import-simple-method-modal
      :is-open="displayImportSimpleMethodModal"
      :loading="submitting"
      :preparing-import="preparingImport"
      :max-steps="importSteps"
      :import-instance-data="importInstanceData"
      :selected-service="selectedService"
      @import-instance="importInstanceStarted"
      @remove-modal="deactivateAll"
    />

    <onboarding-import-advanced-method-modal
      :is-open="displayImportAdvancedMethodModal"
      :preparing-import="preparingImport"
      :loading="submitting"
      :max-steps="importSteps"
      :import-instance-data="importInstanceData"
      :selected-service="selectedService"
      @import-instance="importInstanceStarted"
      @remove-modal="deactivateAll"
    />

    <onboarding-import-instance-setup-complete-modal
      :is-open="displayImportInstanceSetupCompleteModal"
      :completed="importPreparationCompleted"
      :error="importPreparationError"
      :imported-instance="importedInstance"
      :nameservers="nameservers"
      @next="gotoDashboard"
    />

    <onboarding-installation-zero-data-modal
      :is-open="displayZeroDataModal"
      @go-back="deactivateAll"
      @remove-modal="deactivateAll"
    />

    <onboarding-installation-no-type-modal
      :is-open="displayNoInstallationTypeModal"
      @go-back="deactivateAll"
      @remove-modal="deactivateAll"
    />

    <onboarding-installation-limit-modal
      :selectedService="selectedService"
      :isOpen="displayLimitModal"
      @go-back="deactivateAll"
      @remove-modal="deactivateAll"
    />

    <!-- SYNC INSTANCES -->

    <sync-instances-progress-modal
      :is-open="displaySyncInstancesProgressModal"
      :completed="installationCompleted"
      :error="installationError"
      @removeModal="deactivateAll"
      @gotoDashboard="gotoDashboard"
    />
  </div>
</template>

<script>
import Api from "../../apis/Api";
import { deployFromZipUploadMixin } from "./OnboardingDeployFromZipModal.vue";

import OnboardingInstallationLimitModal from "./OnboardingInstallationLimitModal.vue";
import OnboardingInstallationZeroDataModal from "./OnboardingInstallationZeroDataModal.vue";
import OnboardingInstallationNoTypeModal from "./OnboardingInstallationNoTypeModal.vue";
import OnboardingChooseTemplateModal from "./OnboardingChooseTemplateModal";
import ChooseOnboardingProcess from "./ChooseOnboardingProcess.vue";
import OnboardingChooseCreationTypeModal from "./OnboardingChooseCreationTypeModal.vue";
import OnboardingChooseInstallationTypeModal from "./OnboardingChooseInstallationTypeModal.vue";
import OnboardingDeployFromGitModal from "./OnboardingDeployFromGitModal.vue";
import OnboardingDeployFromZipModal from "./OnboardingDeployFromZipModal.vue";
import OnboardingImportUrlModal from "./OnboardingImportUrlModal.vue";
import OnboardingImportAdvancedMethodModal from "./OnboardingImportAdvancedMethodModal.vue";
import OnboardingImportInstanceSetupCompleteModal from "./OnboardingImportInstanceSetupCompleteModal.vue";
import SuperQuickOnboardingMixin from "../../mixins/Onboarding/SuperQuickOnboardingMixin";
import OnboardingShareAccessModal from "./OnboardingShareAccessModal.vue";
import OnboardingChooseUserConfigurableOptionsModal from "./OnboardingChooseUserConfigurableOptionsModal.vue";
import SyncInstancesProgressModal from "./SyncInstancesProgressModal.vue";
import OnboardingStandardFormModal from "./OnboardingStandardFormModal.vue";
import OnboardingChooseServiceModal from "./OnboardingChooseServiceModal.vue";
import OnboardingSelectedServiceMixin from "../../mixins/Onboarding/SelectedServiceMixin";
import OnboardingChooseDomainModal from "./OnboardingChooseDomainModal.vue";
import OnboardingDomainTransferModal from "./OnboardingDomainTransferModal.vue";
import OnboardingDomainInstantVerificationModal
  from "@/components/onboarding/OnboardingDomainInstantVerificationModal.vue";
import OnboardingChooseSiteNameModal from "@/components/onboarding/OnboardingChooseSiteNameModal.vue";
import OnboardingInstallationProgressModal from "@/components/onboarding/OnboardingInstallationProgressModal.vue";
import OnboardingInstallationSuperQuickProgressModal
  from "@/components/onboarding/OnboardingInstallationSuperQuickProgressModal.vue";
import OnboardingChooseImportMethodModal from "@/components/onboarding/OnboardingChooseImportMethodModal.vue";
import OnboardingImportSimpleMethodModal from "@/components/onboarding/OnboardingImportSimpleMethodModal.vue";
import InstallationTypeMixin from "@/mixins/Onboarding/InstallationTypeMixin";

export default {
  components: {
    OnboardingInstallationSuperQuickProgressModal,
    OnboardingInstallationProgressModal,
    OnboardingChooseSiteNameModal,
    OnboardingDomainInstantVerificationModal,
    OnboardingDomainTransferModal,
    OnboardingChooseDomainModal,
    OnboardingChooseServiceModal,
    OnboardingStandardFormModal,
    SyncInstancesProgressModal,
    OnboardingShareAccessModal,
    OnboardingInstallationLimitModal,
    OnboardingInstallationZeroDataModal,
    OnboardingInstallationNoTypeModal,
    OnboardingChooseTemplateModal,
    ChooseOnboardingProcess,
    OnboardingChooseCreationTypeModal,
    OnboardingChooseInstallationTypeModal,
    OnboardingDeployFromGitModal,
    OnboardingDeployFromZipModal,
    OnboardingImportUrlModal,
    OnboardingImportAdvancedMethodModal,
    OnboardingImportInstanceSetupCompleteModal,
    OnboardingChooseUserConfigurableOptionsModal,
    OnboardingChooseImportMethodModal,
    OnboardingImportSimpleMethodModal,
  },
  mixins: [
    SuperQuickOnboardingMixin,
    OnboardingSelectedServiceMixin,
    InstallationTypeMixin,
    deployFromZipUploadMixin,
  ],
  data() {
    return {
      loading: false,
      loadingText: null,
      submitting: false,
      creationType: 0,
      selectedInstallationType: "wordpress",
      onboardingSource: null,
      services: [],
      selectedService: {
        id: 0,
      },
      selectedTheme: {},
      delegatedUserEmail: null,
      selectedDomainName: null,
      selectedSiteName: null,
      sharedAccess: [],
      userConfigurableOptions: {},
      selectedUserConfigurableOptions: {},
      domainTransferDnsRecord: {},
      newInstanceStandardForm: {},
      newDomain: null,
      newInstance: null,
      installedInstance: {},
      installedInstanceDnsMismatch: false,
      installationCompleted: false,
      installationError: false,
      goBack: () => {
        this.gotoDashboard();
      },
      importInstanceData: {
        url: "",
        method: 'plugin',
      },
      importedInstance: {},
      nameservers: [],
      importPreparationCompleted: false,
      importPreparationError: false,
      preparingImport: false,
    };
  },
  methods: {
    startInstallation() {
      // handle browser back button
      history.pushState({}, "", "");
      window.onpopstate = () => {
        if (this.isAnyOpen) {
          this.$store.dispatch("deactivateAll");
          this.resetInstallation();
        }
      };

      this.resetInstallation();

      this.services = this.$store.state.services?.getActiveServices?.() ?? [];
      if (this.services.length === 0) {
        this.$store.dispatch("displayZeroDataModal");
        return;
      }

      if (this.availableInstallationTypes.length === 0) {
        this.$store.dispatch("displayNoInstallationTypeModal");
        return;
      }

      this.selectedInstallationType = this.getFirstInstallationType;
      if (this.availableInstallationTypes.length > 1) {
        this.$store.dispatch("displayChooseInstallationTypeModal");
        return;
      }

      this.installationTypeSelected();
    },

    installationTypeSelected(source = null) {
      if (source) {
        this.selectedInstallationType = source;
      }

      if (this.selectedInstallationType === "wordpress") {
        this.$store.dispatch("displayChooseCreationTypeModal");
        return;
      }

      this.selectedService = this.services[0];
      if (this.services.length > 1) {
        this.$store.dispatch("displayChooseServiceModal");
        return;
      }

      if (this.hasSelectedServiceLimitReached()) {
        this.$store.dispatch("displayLimitModal");
        return;
      }

      if (this.canSelectedServiceShareAccess()) {
        this.$store.dispatch("displayShareAccessModal");
        return;
      }

      this.$store.dispatch("displayWebsiteDomainModal");
    },

    async deployFromGit(payload) {
      this.installationCompleted = false;
      this.installationError = false;
      await this.$store.dispatch("displayInstallationProgress");

      try {
        const sharedAccessEmails = this.sharedAccess.map(item => item.email);

        const response = await Api.post(`/instances/deploy`, {
          service_id: this.selectedService.id,
          domain_id: this.newDomain?.id,
          shared_access: sharedAccessEmails,
          source_type: this.selectedInstallationType,
          repo_url: payload.repo_url ?? null,
          token: payload.token ?? null,
        });

        this.$root.$emit("instance-created", response.data.data);
        this.newInstance = response.data.data;
      } catch (error) {
        this.installationCompleted = true;
        this.installationError = true;
        await this.$store.dispatch("addAlert", {
          success: false,
          errorMessage: Api.getErrorMessage(error),
        });
      }
    },

    async installFromFilesProcess(payload) {
      this.installationCompleted = false;
      this.installationError = false;
      await this.$store.dispatch("displayInstallationProgress");

      try {
        const filePaths = await this.uploadFiles(payload);
        const newInstance = await this.installFromFiles(filePaths);

        this.$root.$emit("instance-created", newInstance);
        this.newInstance = newInstance;
      } catch (error) {
        this.installationCompleted = true;
        this.installationError = true;
        await this.$store.dispatch("addAlert", {
          success: false,
          errorMessage: Api.getErrorMessage(error),
        });
      }
    },

    async uploadFiles(payload) {
      const files = this.normalizePayloadFiles(payload);

      if (!files.length) {
        throw new Error(this.$t("heading.onboarding.zipDeploy.file.empty"));
      }

      const remoteFilepaths = [];

      for (const file of files) {
        const uploadFilename = this.getUploadFilename(
          file.webkitRelativePath || file.name
        );
        const isFromDirectory = !!file.webkitRelativePath;
        const ext = this.getFileExtension(uploadFilename);

        if (!isFromDirectory && !this.isAllowedStandaloneFile(file)) {
          throw new Error(
            this.$t("heading.onboarding.zipDeploy.file.invalidType")
          );
        }

        const response = await Api.post(`/instances/upload-files`, file, {
          headers: {
            "Content-Type": this.getUploadContentType(ext, file),
          },
          params: {
            service_id: this.selectedService.id,
            domain_id: this.newDomain?.id,
            filename: uploadFilename,
          },
        });

        remoteFilepaths.push(response.data.remote_filepath);
      }

      return remoteFilepaths;
    },

    async installFromFiles(filePaths) {
      const sharedAccessEmails = this.sharedAccess.map(item => item.email);
      const response = await Api.post(`/instances/deploy`, {
        service_id: this.selectedService.id,
        domain_id: this.newDomain?.id,
        shared_access: sharedAccessEmails,
        source_type: this.selectedInstallationType,
        remote_filepath: filePaths,
      });
      return response.data.data;
    },

    // WORDPRESS

    creationTypeSelected: function (action, meta = {}) {
      this.resetInstallation();
      this.creationType = action;
      this.onboardingSource = meta?.source || null;

      switch (action) {
        case 0:
          this.newInstanceSelected();
          break;
        case 1:
          this.importStart();
          break;
        default:
          this.$store.dispatch("addAlert", {
            success: false,
            errorMessage: Api.getErrorMessage('Invalid action.'),
          });
      }
    },

    importStart: function () {
      this.services = this.$store.state.services.getActiveServices();
      this.selectedService = this.services[0];

      if (this.services.length > 1) {
        this.$store.dispatch("displayChooseServiceModal");
        return;
      }

      if (this.hasSelectedServiceLimitReached()) {
        this.$store.dispatch("displayLimitModal");
        return;
      }

      this.$store.dispatch("displayImportInstanceUrlSettingsModal");
    },

    async installationStart() {
      // handle browser back button

      history.pushState({}, "", "");
      window.onpopstate = () => {
        if (this.isAnyOpen) {
          this.$store.dispatch("deactivateAll");
          this.resetInstallation();
        }
      };

      this.resetInstallation();
      this.goBack = () => {
        this.gotoDashboard();
      };

      this.services = this.$store.state.services.getActiveServices();
      this.selectedService = this.services[0];

      if (this.isDemo) {
        await this.$store.dispatch("displayChooseOnboardingTypeModal");
        return;
      }

      if (this.services.length > 1) {
        await this.$store.dispatch("displayChooseServiceModal");
        return;
      }

      if (this.hasSelectedServiceLimitReached()) {
        await this.$store.dispatch("displayLimitModal");
        return;
      }

      if (this.canSelectedServiceChooseTheme()) {
        await this.$store.dispatch("displayChooseTemplate");
        return;
      }

      if (this.canSelectedServiceShareAccess()) {
        await this.$store.dispatch("displayShareAccessModal");
        return;
      }

      await this.getInstanceOptions();

      if (this.hasSelectedServiceUserConfigurableOptions()) {
        await this.$store.dispatch("displayChooseUserConfigurableOptionsModal");
        return;
      }

      if (this.hasSelectedServiceStandardOnboarding()) {
        await this.$store.dispatch("displayStandardForm");
        return;
      }

      if (this.hasSelectedServiceAskForDomain()) {
        await this.$store.dispatch("displayWebsiteDomainModal");
        return;
      }

      if (this.hasSelectedServiceAskForSiteName()) {
        await this.$store.dispatch("displayChooseSiteNameModal");
        return;
      }

      if (this.isDemo) {
        await this.handleDemoInstallation()
        return;
      }
      await this.displayInstallationInProgressModal();
      const domainCreated = await this.createDomain();
      if (!domainCreated) {
        this.installationCompleted = true;
        this.installationError = true;
        return;
      }
      await this.installInstance();
    },

    async newInstanceSelected() {
      this.services = this.$store.state.services.getActiveServices();
      this.selectedService = this.services[0];

      if (this.isDemo) {
        await this.$store.dispatch("displayChooseOnboardingTypeModal");
        return;
      }

      if (this.services.length > 1) {
        await this.$store.dispatch("displayChooseServiceModal");
        return;
      }

      if (this.hasSelectedServiceLimitReached()) {
        await this.$store.dispatch("displayLimitModal");
        return;
      }

      if (this.canSelectedServiceChooseTheme()) {
        await this.$store.dispatch("displayChooseTemplate");
        return;
      }

      if (this.canSelectedServiceShareAccess()) {
        await this.$store.dispatch("displayShareAccessModal");
        return;
      }

      await this.getInstanceOptions();

      if (this.hasSelectedServiceUserConfigurableOptions()) {
        await this.$store.dispatch("displayChooseUserConfigurableOptionsModal");
        return;
      }

      if (this.hasSelectedServiceStandardOnboarding()) {
        await this.$store.dispatch("displayStandardForm");
        return;
      }

      if (this.hasSelectedServiceAskForDomain()) {
        await this.$store.dispatch("displayWebsiteDomainModal");
        return;
      }

      if (this.hasSelectedServiceAskForSiteName()) {
        await this.$store.dispatch("displayChooseSiteNameModal");
        return;
      }

      if (this.isDemo) {
        await this.handleDemoInstallation()
        return;
      }

      // create instance after user creation
      if (this.onboardingSource === 'after-login') {
        await this.displayInstallationInProgressModal();
        const domainCreated = await this.createDomain();
        if (!domainCreated) {
          this.installationCompleted = true;
          this.installationError = true;
          return;
        }
        await this.installInstance();
        return;
      }

      const domainCreated = await this.createDomain();
      if (!domainCreated) {
        return;
      }
      await this.displayInstallationInProgressModal();
      await this.installInstance();
    },

    demoOnboardingSelected(classic) {
      this.$root.$emit("app-ready-to-render", true);
      localStorage.removeItem(
        window.location.origin + "-autostartInstallation"
      );
      if (classic) {
        this.$store.dispatch("displayStandardForm");
        return;
      }
      this.$store.dispatch("displayChooseTemplate");
    },

    async serviceSelected(service, quiet = false) {
      this.selectedService = service;
      if (quiet) {
        return;
      }

      if (this.hasSelectedServiceLimitReached()) {
        await this.$store.dispatch("displayLimitModal");
        return;
      }

      if (this.selectedInstallationType !== "wordpress") {
        if (this.canSelectedServiceShareAccess()) {
          await this.$store.dispatch("displayShareAccessModal");
          return;
        }

        await this.$store.dispatch("displayWebsiteDomainModal");
        return;
      }

      if (this.isImport) {
        await this.$store.dispatch("displayImportInstanceUrlSettingsModal");
        return;
      }

      if (this.canSelectedServiceChooseTheme()) {
        await this.$store.dispatch("displayChooseTemplate");
        return;
      }

      if (this.canSelectedServiceShareAccess()) {
        await this.$store.dispatch("displayShareAccessModal");
        return;
      }

      await this.getInstanceOptions();

      if (this.hasSelectedServiceUserConfigurableOptions()) {
        await this.$store.dispatch("displayChooseUserConfigurableOptionsModal");
        return;
      }

      if (this.hasSelectedServiceStandardOnboarding()) {
        await this.$store.dispatch("displayStandardForm");
        return;
      }

      if (this.hasSelectedServiceAskForDomain()) {
        await this.$store.dispatch("displayWebsiteDomainModal");
        return;
      }

      if (this.hasSelectedServiceAskForSiteName()) {
        await this.$store.dispatch("displayChooseSiteNameModal");
        return;
      }

      if (this.isDemo) {
        await this.handleDemoInstallation()
        return;
      }

      const domainCreated = await this.createDomain();
      if (!domainCreated) {
        return;
      }
      await this.displayInstallationInProgressModal();
      await this.installInstance();
    },

    async themeSelected(theme) {
      this.selectedTheme = theme;

      if (this.canSelectedServiceShareAccess()) {
        await this.$store.dispatch("displayShareAccessModal");
        return;
      }

      await this.getInstanceOptions();

      if (this.hasSelectedServiceUserConfigurableOptions()) {
        await this.$store.dispatch("displayChooseUserConfigurableOptionsModal");
        return;
      }

      if (this.hasSelectedServiceStandardOnboarding()) {
        await this.$store.dispatch("displayStandardForm");
        return;
      }

      if (this.hasSelectedServiceAskForDomain()) {
        await this.$store.dispatch("displayWebsiteDomainModal");
        return;
      }

      if (this.hasSelectedServiceAskForSiteName()) {
        await this.$store.dispatch("displayChooseSiteNameModal");
        return;
      }

      if (this.isDemo) {
        await this.handleDemoInstallation()
        return;
      }

      const domainCreated = await this.createDomain();
      if (!domainCreated) {
        return;
      }
      await this.displayInstallationInProgressModal();
      await this.installInstance();
    },

    async sharedAccessSet(assignedUsers) {
      let userEmail = "";
      if (assignedUsers.length > 0) {
        userEmail = assignedUsers[0].email;
      }
      this.delegatedUserEmail = userEmail;
      // unused variable
      this.sharedAccess = assignedUsers;

      if (this.selectedInstallationType !== "wordpress") {
        await this.$store.dispatch("displayWebsiteDomainModal");
        return;
      }

      await this.getInstanceOptions();

      if (this.hasSelectedServiceUserConfigurableOptions()) {
        await this.$store.dispatch("displayChooseUserConfigurableOptionsModal");
        return;
      }

      if (this.isImport) {
        await this.$store.dispatch("displayChooseImportMethodModal");
        return;
      }

      if (this.hasSelectedServiceStandardOnboarding()) {
        await this.$store.dispatch("displayStandardForm");
        return;
      }

      if (this.hasSelectedServiceAskForDomain()) {
        await this.$store.dispatch("displayWebsiteDomainModal");
        return;
      }

      if (this.hasSelectedServiceAskForSiteName()) {
        await this.$store.dispatch("displayChooseSiteNameModal");
        return;
      }

      if (this.isDemo) {
        await this.handleDemoInstallation()
        return;
      }

      const domainCreated = await this.createDomain();
      if (!domainCreated) {
        return;
      }
      await this.displayInstallationInProgressModal();
      await this.installInstance();
    },

    async userConfigurableOptionsSelected(userConfigurableOptions) {
      this.selectedUserConfigurableOptions = userConfigurableOptions;

      if (this.isImport) {
        await this.$store.dispatch("displayChooseImportMethodModal");
        return;
      }

      if (this.hasSelectedServiceStandardOnboarding()) {
        await this.$store.dispatch("displayStandardForm");
        return;
      }

      if (this.hasSelectedServiceAskForDomain()) {
        await this.$store.dispatch("displayWebsiteDomainModal");
        return;
      }

      if (this.hasSelectedServiceAskForSiteName()) {
        await this.$store.dispatch("displayChooseSiteNameModal");
        return;
      }

      if (this.isDemo) {
        await this.handleDemoInstallation()
        return;
      }

      const domainCreated = await this.createDomain();
      if (!domainCreated) {
        return;
      }
      await this.displayInstallationInProgressModal();
      await this.installInstance();
    },

    async domainSelected({domain, existingDomain}) {
      if (this.submitting || this.loading) return;
      this.selectedDomainName = domain;

      if (this.isSelectedServiceServerAllowTransferDomain()) {
        let result = await this.checkDomainAvailability();
        if (result.verification_required) {
          this.domainTransferDnsRecord = result.verification_record;
          await this.$store.dispatch("displayDomainTransferModal");
          return;
        }
      }

      if (existingDomain) {
        this.newDomain = existingDomain;
      } else {
        const domainCreated = await this.createDomain();
        if (!domainCreated) {
          return;
        }
      }

      if (this.hasSelectedServiceInstantVerificationEnabled()) {
        let result = await this.checkDnsPropagation();
        if (!result) {
          await this.$store.dispatch("displayDomainDnsVerificationModal");
          return;
        }
      }

      if (this.selectedInstallationType === "git") {
        await this.$store.dispatch("displayDeployFromGitModal");
        return;
      }

      if (this.selectedInstallationType === "files") {
        await this.$store.dispatch("displayDeployFromZipModal");
        return;
      }

      if (this.hasSelectedServiceAskForSiteName()) {
        await this.$store.dispatch("displayChooseSiteNameModal");
        return;
      }

      if (this.isDemo) {
        await this.handleDemoInstallation()
        return;
      }
      await this.displayInstallationInProgressModal();
      await this.installInstance();
    },

    async transferDomain() {
      const domainCreated = await this.createDomain();
      if (!domainCreated) return;

      if (this.hasSelectedServiceInstantVerificationEnabled()) {
        let result = await this.checkDnsPropagation();
        if (!result) {
          await this.$store.dispatch("displayDomainDnsVerificationModal");
          return;
        }
      }

      if (this.selectedInstallationType === "git") {
        await this.$store.dispatch("displayDeployFromGitModal");
        return;
      }

      if (this.selectedInstallationType === "files") {
        await this.$store.dispatch("displayDeployFromZipModal");
        return;
      }

      if (this.hasSelectedServiceAskForSiteName()) {
        await this.$store.dispatch("displayChooseSiteNameModal");
        return;
      }

      if (this.isDemo) {
        await this.handleDemoInstallation()
        return;
      }
      await this.displayInstallationInProgressModal();
      await this.installInstance();
    },

    async domainVerified() {
      if (this.selectedInstallationType === "git") {
        await this.$store.dispatch("displayDeployFromGitModal");
        return;
      }

      if (this.selectedInstallationType === "files") {
        await this.$store.dispatch("displayDeployFromZipModal");
        return;
      }

      if (this.hasSelectedServiceAskForSiteName()) {
        await this.$store.dispatch("displayChooseSiteNameModal");
        return;
      }

      if (this.isDemo) {
        await this.handleDemoInstallation()
        return;
      }
      await this.displayInstallationInProgressModal();
      await this.installInstance();
    },

    async siteNameSelected(siteName) {
      this.selectedSiteName = siteName;

      if (this.isDemo) {
        await this.handleDemoInstallation()
        return;
      }

      if (this.newDomain === null) {
        const domainCreated = await this.createDomain();
        if (!domainCreated) {
          return;
        }
      }
      await this.displayInstallationInProgressModal();
      await this.installInstance();
    },

    async installInstanceFromStandardForm(form, existingDomain) {
      if (this.submitting || this.loading) return;
      this.selectedDomainName = form.domain;
      this.selectedSiteName = form.name;
      this.newInstanceStandardForm = form;

      if (this.isSelectedServiceServerAllowTransferDomain()) {
        let result = await this.checkDomainAvailability();
        if (result.verification_required) {
          this.domainTransferDnsRecord = result.verification_record;
          await this.$store.dispatch("displayDomainTransferModal");
          return;
        }
      }

      if (existingDomain) {
        this.newDomain = existingDomain;
      } else {
        const domainCreated = await this.createDomain();
        if (!domainCreated) {
          this.installationCompleted = true;
          this.installationError = true;
          return;
        }
      }

      if (this.hasSelectedServiceInstantVerificationEnabled()) {
        let result = await this.checkDnsPropagation();
        if (!result) {
          await this.$store.dispatch("displayDomainDnsVerificationModal");
          return;
        }
      }

      if (this.isDemo) {
        await this.handleDemoInstallation()
        return;
      }
      await this.displayInstallationInProgressModal();
      await this.installInstance();
    },

    async displayInstallationInProgressModal() {
      if (this.hasSelectedServiceSuperQuickOnboarding()) {
        await this.$store.dispatch("displaySuperQuickInstallationProgress");
      } else {
        await this.$store.dispatch("displayInstallationProgress");
      }
    },

    async getInstanceOptions() {
      this.submitting = true;
      this.loadingText = 'button.onboarding.preparing-installation';
      try {
        const response = await Api.get(`/services/${this.selectedService.id}/instance-options`);
        this.userConfigurableOptions = response.data.data.user_configurable_options;

      } catch (error) {
        await this.$store.dispatch("addAlert", {
          success: false,
          errorMessage: Api.getErrorMessage(error),
        });
      } finally {
        // for UX
        await new Promise(resolve => setTimeout(resolve, 1500));
        this.submitting = false;
        this.loadingText = null;
      }
    },

    async createDomain() {
      this.submitting = true;
      this.loadingText = 'button.onboarding.create-domain';
      try {
        const response = await Api.post(`/services/${this.selectedService.id}/domains`, {
          domain: this.selectedDomainName,
          name: this.selectedSiteName,
          delegated_user_email: this.delegatedUserEmail,
          user_configurable_options: this.selectedUserConfigurableOptions,
        });

        this.newDomain = response.data.data;

        return true;
      } catch (error) {
        await this.$store.dispatch("addAlert", {
          success: false,
          errorMessage: Api.getErrorMessage(error),
        });

        return false;
      } finally {
        this.submitting = false;
        this.loadingText = null;
      }
    },

    async installInstance() {
      try {
        const sharedAccessEmails = this.sharedAccess.map(item => item.email);

        const siteDescriptionStorageKey = window.location.origin + "-new-site-description";
        const siteDescriptionRaw = localStorage.getItem(siteDescriptionStorageKey);
        const siteDescription =
          typeof siteDescriptionRaw === "string" ? siteDescriptionRaw.trim() : "";

        const response = await Api.post(`/instances/install`, {
          service_id: this.selectedService?.id,
          domain_id: this.newDomain?.id,
          install_method: this.selectedService?.install_method,
          name: this.selectedSiteName,
          theme: this.selectedTheme?.slug,
          shared_access: sharedAccessEmails,
          ...this.newInstanceStandardForm,
          ...(siteDescription ? {"site_description": siteDescription} : {}),
        });

        this.$root.$emit("instance-created", response.data.data);
        this.newInstance = response.data.data;
        if (siteDescription) {
          localStorage.removeItem(siteDescriptionStorageKey);
        }
        return response.data.data;
      } catch (error) {
        await this.$store.dispatch("addAlert", {
          success: false,
          errorMessage: Api.getErrorMessage(error),
        });
      }
    },

    async handleDemoInstallation() {
      let instanceName = this.selectedSiteName ?? this.selectedDomainName;

      await this.$store.dispatch("displayInstallationProgress");
      setTimeout(() => {
        this.installationCompleted = true;
        this.$store.dispatch("addAlert", {
          success: true,
          successMessage: `Instance "${instanceName}" installed successfully`,
        });
      }, 3000);
    },

    retryInstallInstance(instanceId, params) {
      if (this.submitting) return;
      if (!instanceId) {
        instanceId = this.installedInstance.id;
      }
      if (!params) {
        params = this.installedInstance.install_details;
      }
      this.submitting = true;
      this.installationCompleted = false;
      this.installationError = false;
      Api.put(`/instances/${instanceId}/retry-install`, params)
        .then((response) => {
          this.$root.$emit("instance-item-changed", response.data.data);
          if (this.displayInstallationProgress) {
            this.$store.dispatch("displayInstallationProgress");
          } else if (this.displaySuperQuickInstallationProgress) {
            this.$store.dispatch("displaySuperQuickInstallationProgress");
          } else {
            this.gotoDashboard();
          }
        })
        .catch((error) => {
          this.installationCompleted = true;
          this.installationError = true;
          this.$store.dispatch("addAlert", {
            success: false,
            errorMessage: Api.getErrorMessage(error),
          });
        }).finally(() => {
        this.submitting = false;
      })
    },

    // IMPORT WORDPRESS

    async importedInstanceUrlSelected(payload) {
      this.importInstanceData = {
        ...this.importInstanceData,
        ...(payload?.url ? {url: payload.url} : {}),
        ...(payload?.ip_address ? {ip_address: payload.ip_address} : {}),
      };

      if (this.canSelectedServiceShareAccess()) {
        await this.$store.dispatch("displayShareAccessModal");
        return;
      }

      await this.getInstanceOptions();

      if (this.hasSelectedServiceUserConfigurableOptions()) {
        await this.$store.dispatch("displayChooseUserConfigurableOptionsModal");
        return;
      }

      await this.$store.dispatch("displayChooseImportMethodModal");
    },

    importMethodSelected(method) {
      this.importInstanceData.method = method;

      if (method === 'plugin') {
        this.$store.dispatch("displayImportSimpleMethodModal");
        return;
      }

      this.$store.dispatch("displayImportAdvancedMethodModal");
    },

    async importInstanceStarted(importDetails) {
      if (this.preparingImport) return;
      this.importInstanceData = {
        ...this.importInstanceData,
        ...importDetails,
      };

      if (this.isDemo) {
        await this.handleDemoImport()
        return;
      }

      this.preparingImport = true;
      const domainCreated = await this.createImportDomain();
      if (!domainCreated) {
        this.preparingImport = false;
        return;
      }

      const importData = await this.importInstance();
      this.preparingImport = false;
      this.importPreparationCompleted = true;
      if (!importData) {
        this.importPreparationError = true;
        await this.$store.dispatch("displayImportInstanceSetupCompleteModal");
        return;
      }

      this.importedInstance = importData.data;
      this.nameservers = importData.meta.nameservers;
      await this.$store.dispatch("displayImportInstanceSetupCompleteModal");
    },

    async handleDemoImport() {
      const response = await this.importDemoInstance();

      this.importedInstance = response.data;
      this.nameservers = response.meta.nameservers;
      this.$root.$emit("instance-created", response.data);
      await this.$store.dispatch("displayImportInstanceSetupCompleteModal");
    },

    async createImportDomain() {
      try {
        const response = await Api.post(`/instances/import/domain`, {
          service_id: this.selectedService.id,
          url: this.importInstanceData.url,
          delegated_user_email: this.delegatedUserEmail,
          user_configurable_options: this.selectedUserConfigurableOptions,
        });

        this.newDomain = response.data.data;

        return true;
      } catch (error) {
        await this.$store.dispatch("addAlert", {
          success: false,
          errorMessage: Api.getErrorMessage(error),
        });

        return false;
      }
    },

    async importInstance() {
      try {
        const response = await Api.post(`/instances/import/import`, {
          ...this.importInstanceData,
          service_id: this.selectedService?.id,
          domain_id: this.newDomain?.id,
          type: this.importInstanceData.method,
          shared_access: this.sharedAccess.map(item => item.email),
        });

        return response.data;
      } catch (error) {
        await this.$store.dispatch("addAlert", {
          success: false,
          errorMessage: Api.getErrorMessage(error),
        });
        return false;
      }
    },

    async importDemoInstance() {
      return new Promise((resolve, reject) => {
        if (this.importInstanceData.url !== "reject") {
          setTimeout(() => {
            resolve({
              data: {url: "https://example.com"},
              meta: {
                nameservers: ["ns1.example.com", "ns2.example.com", "ns3.example.com"],
              },
            });
          }, 2000);
        } else {
          reject(false);
        }
      });
    },

    retryImportWebsite(instanceId) {
      this.installationCompleted = false;
      this.installationError = false;
      Api.put(`/instances/${instanceId}/import/quick-retry`)
        .then((response) => {
          this.$root.$emit("instance-item-changed", response.data.data);
        })
        .catch((error) => {
          this.installationCompleted = true;
          this.installationError = true;
          this.$store.dispatch("addAlert", {
            success: false,
            errorMessage: Api.getErrorMessage(error),
          });
        });
    },

    // OTHER

    changeThemeButtonClicked() {
      this.selectedTheme = {};
      this.$store.dispatch("displayChooseTemplate");
    },

    changeServiceButtonClicked() {
      this.selectedDomainName = null;
      this.newDomain = null;
      this.selectedService = this.services[0];
      this.$store.dispatch("displayChooseServiceModal");
    },

    gotoDashboard: async function () {
      if (this.isImport) {
        const refreshQuery = {
          ...this.$route.query,
          reload: Date.now().toString(),
        };
        await this.$router.replace({name: "Instances", query: refreshQuery});
        await this.$store.dispatch("deactivateAll");
        this.resetInstallation();
        return;
      }

      if (this.$route.name !== "Instances") {
        await this.$router.push({name: "Instances"});
      }
      await this.$store.dispatch("deactivateAll");
      this.resetInstallation();
    },

    deactivateAll: function () {
      this.selectedTheme = {};
      this.selectedService = {id: 0};
      this.selectedDomainName = null;
      this.selectedSiteName = null;
      this.selectedInstallationType = "wordpress";
      this.$store.dispatch("deactivateAll");
    },

    resetInstallation: function () {
      this.installationCompleted = false;
      this.installationError = false;
      this.installedInstance = {};
      this.installedInstanceDnsMismatch = false;
      this.selectedTheme = {};
      this.selectedService = {id: 0};
      this.selectedDomainName = null;
      this.selectedSiteName = null;
      this.delegatedUserEmail = null;
      this.sharedAccess = [];
      this.selectedUserConfigurableOptions = {};
      this.domainTransferDnsRecord = {};
      this.newInstanceStandardForm = {};
      this.newDomain = null;
      this.newInstance = null;
      this.onboardingSource = null;
      this.selectedInstallationType = "wordpress";

      this.importInstanceData = {
        url: "",
        method: 'plugin',
      };
      this.importPreparationCompleted = false;
      this.importPreparationError = false;
    },

    async checkDomainAvailability() {
      this.loading = true;
      this.loadingText = 'button.onboarding.check-domain-availability';
      return Api.get(
        `/services/${this.selectedService.id}/domains/verify?domain=${this.selectedDomainName}`
      )
        .then((response) => {
          return response.data;
        })
        .catch((error) => {
          this.$store.dispatch("addAlert", {
            success: false,
            errorMessage: Api.getErrorMessage(error),
          });
        })
        .finally(() => {
          this.loading = false;
          this.loadingText = null;
        });
    },

    async checkDnsPropagation() {
      this.loading = true;
      let result = await Api.dns().verifyDomainResolving(
        this.selectedDomainName,
        null,
        this.selectedService.onboarding.dns_settings,
        this.selectedService.dns_server.enabled,
        this.selectedService.hosting_server.ip_v4_addresses,
        this.selectedService.dns_server.nameservers,
        this.selectedService.hosting_accounts.flatMap(account => account.host_c_names),
        [],
        this.selectedService.onboarding.eager_domain_dns_verification_enabled,
      );
      this.loading = false;
      return result;
    },

    startInstanceSynchronizationProcess() {
      this.$store.dispatch("displaySyncInstancesProgressModal");
    },

    handleBeforeUnload(e) {
      if (!this.submitting) {
        return;
      }
      const message = 'Installation is in progress. Leaving the page may cause the process to fail.';
      (e || window.event).returnValue = message;
      return message;
    },
  },
  created: function () {
    if (this.isAutoStartEnabled) {
      this.installationStart();
    }

    this.$root.$on("instance-installation-finished", async (data) => {
      this.installedInstance = data;
      if (this.selectedService.install_method === "super_quick") {
        if (
          this.selectedService.onboarding
            .super_quick_post_installation_action === "redirect"
        ) {
          await this.wpAdminSsoRedirect();
          return;
        } else if (
          this.selectedService.onboarding
            .super_quick_post_installation_action === "dns_verification"
        ) {
          this.installedInstanceDnsMismatch =
            !(await Api.dns().verifyDomainResolving(
              this.installedInstance.domain,
              this.installedInstance.onboarding_subdomain,
              this.installedInstance.dns_settings,
              this.installedInstance.service_has_dns,
              this.installedInstance.host_ipv4_addresses,
              this.installedInstance.domain_zone?.details.nameservers ?? [],
              this.installedInstance.host_c_names ?? [],
              this.installedInstance.domain_zone?.details?.configuration_records ?? []
            ));
          this.showStartCreatingButton = true;
          this.installationCompleted = true;
          this.installationError = false;
          return;
        }
      }
      this.showStartCreatingButton = true;
      this.installationCompleted = true;
      this.installationError = false;
    });

    this.$root.$on("instance-installation-failed", (data) => {
      this.installationCompleted = true;
      this.installationError = true;
      this.installedInstance = data;
    });

    this.$root.$on("service-sync-hosting-account-failed", () => {
      this.installationCompleted = true;
      this.installationError = true;
    });

    this.$root.$on("service-sync-hosting-account-finished", () => {
      this.installationCompleted = true;
      this.installationError = false;
    });
  },
  mounted: function () {
    window.addEventListener('beforeunload', this.handleBeforeUnload);

    this.$root.$on("start-installation-process", () => {
      this.startInstallation();
    })

    this.$root.$on("instance-retry-install", (instance) => {
      if (instance.import_details !== null) {
        this.retryImportWebsite(instance.id);
        return;
      }
      let params = instance.install_details;
      if (params.install_method === "standard") {
        this.$root.$emit("instance-retry-install-with-form", instance);
        this.$store.dispatch("displayStandardForm");
      } else {
        this.retryInstallInstance(instance.id, params);
      }
    });
  },
  beforeDestroy() {
    window.removeEventListener('beforeunload', this.handleBeforeUnload);
  },
  watch: {
    isAutoStartEnabled(enabled) {
      if (enabled) {
        this.$root.$emit("app-ready-to-render", false);
        this.installationStart();
        return;
      }
      this.$root.$emit("app-ready-to-render", true);
    },
    isAnyOpen: function (value) {
      if (value) {
        setTimeout(() => {
          document.querySelector("html").classList.add("overflow-y-hidden");
        }, 300);
      } else {
        setTimeout(() => {
          document.querySelector("html").classList.remove("overflow-y-hidden");
        }, 150);
      }
    },
  },
  computed: {
    isAnyOpen: function () {
      if (
        this.displayImportInstanceSetupCompleteModal ||
        this.displayImportAdvancedMethodModal ||
        this.displayImportSimpleMethodModal ||
        this.displayImportUrlModal ||
        this.displayChooseCreationTypeModal ||
        this.displayChooseInstallationTypeModal ||
        this.displayDeployFromGitModal ||
        this.displayDeployFromZipModal ||
        this.displayChooseOnboardingTypeModal ||
        this.displaySelectProductModal ||
        this.displayInstallationProgress ||
        this.displaySuperQuickInstallationProgress ||
        this.displayLimitModal ||
        this.displayZeroDataModal ||
        this.displayNoInstallationTypeModal ||
        this.displayChooseTemplate ||
        this.displayStandardForm ||
        this.displayWebsiteDomainModal ||
        this.displayChooseUserConfigurationOptionsModal ||
        this.displayWebsiteInstallationModal ||
        this.displayDomainTransferModal ||
        this.displayChooseUserModal ||
        this.displayShareAccessModal ||
        this.displayChooseUserConfigurableOptionsModal ||
        this.displaySyncInstancesProgressModal ||
        this.displayDomainDnsVerificationModal ||
        this.displayChooseServiceModal ||
        this.displayChooseSiteNameModal ||
        this.displayChooseImportMethodModal
      ) {
        return true;
      } else {
        return false;
      }
    },
    isDemo() {
      return (
        typeof this.$store.state.home.user !== "undefined" &&
        this.$store.state.home.user.email == "demo@panelalpha.com"
      );
    },
    isAutoStartEnabled() {
      return (
        this.isDemo &&
        localStorage.getItem(window.location.origin + "-autostartInstallation")
      );
    },
    displayImportInstanceSetupCompleteModal: function () {
      return this.$store.state.InstallationModule
        .displayImportInstanceSetupCompleteModal;
    },
    displayImportAdvancedMethodModal: function () {
      return this.$store.state.InstallationModule
        .displayImportAdvancedMethodModal;
    },
    displayImportSimpleMethodModal: function () {
      return this.$store.state.InstallationModule
        .displayImportSimpleMethodModal;
    },
    displayImportUrlModal: function () {
      return this.$store.state.InstallationModule
        .displayImportInstanceUrlSettingsModal;
    },
    displayChooseCreationTypeModal: function () {
      return this.$store.state.InstallationModule
        .displayChooseCreationTypeModal;
    },
    displayChooseInstallationTypeModal: function () {
      return this.$store.state.InstallationModule
        .displayChooseInstallationTypeModal;
    },
    displayDeployFromGitModal: function () {
      return this.$store.state.InstallationModule.displayDeployFromGitModal;
    },
    displayDeployFromZipModal: function () {
      return this.$store.state.InstallationModule.displayDeployFromZipModal;
    },
    displayChooseOnboardingTypeModal: function () {
      return this.$store.state.InstallationModule
        .displayChooseOnboardingTypeModal;
    },
    displaySelectProductModal: function () {
      return this.$store.state.InstallationModule.displaySelectProductModal;
    },
    displayInstallationProgress: function () {
      return this.$store.state.InstallationModule.displayInstallationProgress;
    },
    displaySuperQuickInstallationProgress: function () {
      return this.$store.state.InstallationModule
        .displaySuperQuickInstallationProgress;
    },
    displayLimitModal: function () {
      return this.$store.state.InstallationModule.displayLimitModal;
    },
    displayZeroDataModal: function () {
      return this.$store.state.InstallationModule.displayZeroDataModal;
    },
    displayNoInstallationTypeModal: function () {
      return this.$store.state.InstallationModule.displayNoInstallationTypeModal;
    },
    displayChooseTemplate: function () {
      return this.$store.state.InstallationModule.displayChooseTemplate;
    },
    displayStandardForm: function () {
      return this.$store.state.InstallationModule.displayStandardForm;
    },
    displayWebsiteInstallationModal: function () {
      return this.$store.state.InstallationModule
        .displayWebsiteInstallationModal;
    },
    displayWebsiteDomainModal: function () {
      return this.$store.state.InstallationModule.displayWebsiteDomainModal;
    },
    displayDomainTransferModal() {
      return this.$store.state.InstallationModule
        .displayDomainTransferModal;
    },
    displayChooseUserModal() {
      return this.$store.state.InstallationModule.displayChooseUserModal;
    },
    displayShareAccessModal() {
      return this.$store.state.InstallationModule.displayShareAccessModal;
    },
    displayChooseUserConfigurableOptionsModal() {
      return this.$store.state.InstallationModule
        .displayChooseUserConfigurableOptionsModal;
    },
    displaySyncInstancesProgressModal() {
      return this.$store.state.InstallationModule
        .displaySyncInstancesProgressModal;
    },
    displayDomainDnsVerificationModal() {
      return this.$store.state.InstallationModule
        .displayDomainDnsVerificationModal;
    },
    displayChooseServiceModal() {
      return this.$store.state.InstallationModule
        .displayChooseServiceModal;
    },
    displayChooseSiteNameModal() {
      return this.$store.state.InstallationModule
        .displayChooseSiteNameModal;
    },
    displayChooseImportMethodModal() {
      return this.$store.state.InstallationModule
        .displayChooseImportMethodModal;
    },
    isImport() {
      return this.creationType === 1;
    },
    importSteps() {
      let steps = 4;

      if (this.canSelectedServiceShareAccess()) {
        steps++;
      }

      if (this.hasSelectedServiceUserConfigurableOptions()) {
        steps++;
      }

      return steps;
    }
  },
};
</script>

<style></style>
